/**
 * The English deck — the SOURCE OF RECORD for every representative-visible string in svapp.
 *
 * Rules (vCIP_droom_4.0/src/i18n/decks/flow.ts, ratified by INTAKE-08, carried forward):
 *   • FULL-SENTENCE keys, never fragment joins — Hindi word order differs.
 *   • At most one `{slot}` per key. The single irreducible exception is `sv.capture.progress`
 *     ("Step X of Y"), exactly as the droom's reconnect-progress exception.
 *   • `**bold**` markers survive translation (rich() renders them).
 *   • Tone: plain, warm, second person, no jargon, no blame, never a score or a threshold.
 *     "Verified" stays a RESERVED word on this surface; accreditation vocabulary is banned in
 *     both directions (DEC-073; src/__tests__/dec073Vocabulary.test.ts).
 *   • Prompt TEXT for each shot is the SERVER's (`script[].promptTexts`, resolved per locale by
 *     `shotText()` — FSD-18 D1) and is rendered verbatim; the deck carries only the chrome around it.
 */

export const SV_COPY = {
  // ── shared actions & chrome ────────────────────────────────────────────────
  'sv.subtitle': 'Site verification',
  'sv.action.continue': 'Continue',
  'sv.action.tryAgain': 'Try again',
  'sv.action.retake': 'Retake',
  'sv.action.back': 'Back',
  'sv.action.useThis': 'Use this',
  'sv.action.useAnyway': 'Use it anyway',
  'sv.action.allowed': "I've allowed access",
  'sv.action.copyLink': 'Copy verification link',
  'sv.action.linkCopied': 'Link copied ✓',
  'sv.action.startRecording': 'Start recording',
  'sv.action.stopRecording': 'Stop recording',
  'sv.action.takePhoto': 'Take photo',
  'sv.action.flipCamera': 'Switch camera',
  'sv.action.turnOnCamera': 'Turn on the camera',
  'sv.action.submit': 'Submit',
  'sv.action.reRecord': 'Record this again',
  'sv.action.skipShot': 'Skip this shot',
  'sv.pill.secure': 'Secure',
  'sv.pill.submitted': 'Submitted',
  'sv.poweredBy': 'Powered by KYC Hub',
  'sv.error.tryAgain': 'Please try again.',

  // ── entry / shell ──────────────────────────────────────────────────────────
  'sv.entry.preparing': 'Opening your verification request…',
  'sv.entry.linkRequired.title': 'Open your verification link',
  'sv.entry.linkRequired.body':
    'To start or continue, open the secure link you were sent by SMS or email.',
  'sv.entry.notFound.title': "This link isn't valid",
  'sv.entry.notFound.body': 'Please use the latest link from your SMS or email.',
  'sv.entry.expired.title': 'This link has expired',
  'sv.entry.expired.body':
    'Links are valid for a limited time. Ask the organisation that sent it to send you a new one.',
  'sv.entry.closed.title': 'This request is no longer open',
  'sv.entry.closed.body':
    'It may already have been submitted or withdrawn. If you were expecting to record something, contact the organisation that sent the link.',
  'sv.entry.unreachable.title': "We couldn't open your request",
  'sv.entry.unreachable.body': 'Check your connection and try again. Nothing has been recorded yet.',

  // ── front door (confirm the request) ───────────────────────────────────────
  'sv.door.title': 'Confirm this request',
  'sv.door.sub': 'Before anything is recorded, check that this request is what you expected.',
  'sv.door.client': 'Requested by',
  'sv.door.purpose': 'Purpose',
  // The link's purpose word (`sv_capture_link.purpose`, V170 CHECK) — never the raw value on screen (FSD-18 D7).
  'sv.door.purpose.CAPTURE': 'Video check of your premises',
  'sv.door.purpose.RECAPTURE': 'Record a few shots again',
  'sv.door.purpose.REVISIT': 'A follow-up visit to your premises',
  'sv.door.purpose.REVERIFY': 'A fresh check of your premises',
  'sv.door.purpose.FIELD_VISIT': 'A visit by a field agent',
  'sv.door.purpose.other': 'A check of your premises',
  'sv.door.reference': 'Reference',
  'sv.door.premises': 'Premises',
  'sv.door.kind.OFFICE': 'Office',
  'sv.door.kind.SHOP': 'Shop',
  'sv.door.kind.FACTORY': 'Factory',
  'sv.door.kind.WAREHOUSE': 'Warehouse',
  'sv.door.kind.RESIDENCE': 'Home',
  'sv.door.kind.OTHER': 'Premises',
  'sv.door.captureList': 'What you will be asked to capture',
  'sv.door.time': 'It takes about 12 to 20 minutes, on this phone, at the premises.',
  'sv.door.never': '{tenantName} will never ask for OTPs, passwords or documents outside this page.',
  'sv.door.neverGeneric':
    'The client will never ask for OTPs, passwords or documents outside this page.',
  'sv.door.concern.trigger': 'Something looks wrong? Report a concern',
  'sv.door.concern.title': 'If this request looks wrong',
  'sv.door.concern.body':
    'Do not continue. Contact {tenantName} through a channel you already trust — a number or address you had before this message, not one from this page.',
  'sv.door.concern.body2':
    'Nothing is recorded until you continue, and no one from the client will ask you for a code, a password or a document photo outside these steps.',
  'sv.door.concern.stop': 'Stop and close this page',
  'sv.door.concern.stopped.title': 'Nothing was recorded',
  'sv.door.concern.stopped.body':
    'You can close this page. If you decide to continue later, open the same link again.',
  'sv.door.cta': 'This is what I expected — continue',

  // ── OTP front door (SPEC-15 / S15-1): the code sent to the invited email / mobile ──
  'sv.otp.title': 'Enter the code we sent you',
  'sv.otp.sent.EMAIL': 'We sent a 6-digit code to {target}. Check your inbox, including the spam folder.',
  'sv.otp.sent.SMS': 'We sent a 6-digit code by SMS to {target}.',
  'sv.otp.validFor': 'The code works for 10 minutes, and only on this page.',
  'sv.otp.label': '6-digit code',
  'sv.otp.verify': 'Confirm the code',
  'sv.otp.verifying': 'Checking the code…',
  'sv.otp.never':
    'We will never ask you for this code by phone, message or in person. Enter it only here.',
  'sv.otp.resend': "Didn't get a code? Ask the organisation that sent you the link to send it again.",
  'sv.otp.wrong': "That code isn't right. Check it and try again.",
  'sv.otp.wrong.attempts': "That code isn't right. You have {n} more tries before this link is locked.",
  'sv.otp.wrong.last': "That code isn't right. You have 1 more try — after that this link will be locked.",
  'sv.otp.locked.title': 'This link is locked',
  'sv.otp.locked.body':
    'Too many incorrect codes were entered, so this link no longer works. The organisation must send you a new one. Nothing has been recorded.',
  'sv.otp.expired.title': 'That code has expired',
  'sv.otp.expired.body': 'Codes work for 10 minutes. Ask the organisation that sent you the link to send it again.',
  'sv.otp.unavailable.title': "We couldn't send you a code",
  'sv.otp.unavailable.body':
    "The way this organisation sends codes isn't available right now, so this link can't be opened yet. Nothing has been recorded. Contact the organisation through a channel you already trust.",
  'sv.otp.unreachable': "We couldn't check the code. Check your connection and try again.",
  'sv.otp.failed': "We couldn't check the code right now. Please try again.",

  // ── consent ────────────────────────────────────────────────────────────────
  'sv.consent.title': 'Your consent',
  'sv.consent.sub':
    'Choose what {tenantName} may record and use. Items marked required are needed for this verification.',
  'sv.consent.notice': 'Notice version {version}',
  'sv.consent.required': 'Required',
  'sv.consent.optional': 'Optional',
  'sv.consent.purpose.PREMISES_VIDEO.t': 'Video and photos of the premises',
  'sv.consent.purpose.PREMISES_VIDEO.d':
    'Short clips and photos of the outside and inside of the premises, kept as the record of this verification.',
  'sv.consent.purpose.PERSONS_IN_FRAME.t': 'People who appear in the recording',
  'sv.consent.purpose.PERSONS_IN_FRAME.d':
    'Your face for the selfie, and anyone who happens to be in frame. Faces other than yours are blurred on shared copies.',
  'sv.consent.purpose.GEOLOCATION.t': 'Your location while recording',
  'sv.consent.purpose.GEOLOCATION.d':
    "The phone's location at each shot, compared with the registered address.",
  'sv.consent.purpose.IDENTITY_DOCUMENT.t': 'Your identity document',
  'sv.consent.purpose.IDENTITY_DOCUMENT.d':
    'A photo of your ID, used only to confirm you are the person representing these premises.',
  'sv.consent.purpose.LOCATION.t': 'Your location while recording',
  'sv.consent.purpose.LOCATION.d':
    "The phone's location at each shot — and how it was obtained — compared with the registered address.",
  'sv.consent.purpose.REGISTRY_CROSS_MATCH.t': 'Checking public registry records',
  'sv.consent.purpose.REGISTRY_CROSS_MATCH.d':
    'Business identifiers already given to {tenantName} (such as GSTIN or Udyam) are used only to look up the matching public record.',
  'sv.consent.purpose.DATA_RETENTION.t': 'Keeping the sealed record',
  'sv.consent.purpose.DATA_RETENTION.d':
    'The recording and the data above are kept, sealed and unaltered, for the period stated in the notice, then deleted.',
  'sv.consent.noticeFull': 'The full notice',
  'sv.consent.noticeControlling': '(the English text is the reference version)',
  // VS-08: a tenant may elect another controlling language (the text it ratified) — the note names it
  'sv.consent.noticeControlling.ar': '(the Arabic text is the reference version)',
  'sv.consent.purpose.ONWARD_SHARING.t': 'Sharing the result onward',
  'sv.consent.purpose.ONWARD_SHARING.d':
    'The verification report and recordings may be shared with the regulated institution {tenantName} is verifying on behalf of.',
  'sv.consent.residence':
    'This is a home. The recording will show where you live. Precise street-level location is not shown on shared copies, and the recording is used only to confirm this address.',
  // VS-02: a DWELLING kind that is not a home (worker accommodation) — the server's privacy class picks this card
  'sv.consent.dwelling':
    'This is accommodation where people live. Film only the entrance and the shared areas — never bedrooms, bed spaces or the people who live here. Shared copies show only the area, never the exact location.',
  'sv.consent.bystander.t': 'Keep other people out of frame',
  'sv.consent.bystander.d':
    'Before you record inside, ask anyone who does not need to be in the recording to step aside. Record people only where you cannot avoid it.',
  'sv.consent.retention':
    'Recordings are kept for the period the organisation has set. You can ask them about correction or erasure once the verification purpose ends.',
  'sv.consent.affirm':
    'I **consent** to {tenantName} recording and using the items I have allowed above, for this verification.',
  'sv.consent.agree': 'Agree and continue',
  'sv.consent.recording': 'Recording your consent…',
  'sv.consent.recorded': 'Consent recorded',
  'sv.consent.declined.title': 'This verification cannot continue',
  'sv.consent.declined.body':
    'A required item was not allowed: {purpose}. Without it this verification cannot be completed this way. You can change your choice, or close this page and contact the organisation for another option.',
  'sv.consent.declined.review': 'Review my choices',
  'sv.consent.postfail': "We couldn't record your consent. Check your connection and try again.",
  // ── SV-5-2 second round: the three live-call purposes (v2 notice, `liveLeg: true`), in their own group ──
  'sv.consent.live.group': 'For the live video call with an officer',
  'sv.consent.purpose.LIVE_VIDEO.t': 'Live video of you is recorded',
  'sv.consent.purpose.LIVE_VIDEO.d':
    'An officer from {tenantName} sees and speaks with you on a recorded video call; your face and voice are part of the recording.',
  'sv.consent.purpose.LIVE_IDENTITY_CHECK.t': 'A liveness check and a face comparison',
  'sv.consent.purpose.LIVE_IDENTITY_CHECK.d':
    'Software checks that the video shows a live person and compares your face with the selfie you already took. A person decides the outcome.',
  'sv.consent.purpose.LIVE_CUSTODY_RETENTION.t': "The call recording is kept under {tenantName}'s video-call retention rules",
  'sv.consent.purpose.LIVE_CUSTODY_RETENTION.d': 'Not under the site-verification rules above. The period is stated in the full notice.',

  // ── capture flow ───────────────────────────────────────────────────────────
  'sv.capture.preparing': 'Preparing your capture steps…',
  'sv.capture.startFail.title': "We couldn't load your capture steps",
  'sv.capture.startFail.body': 'Please check your connection and try again. Nothing is lost.',
  'sv.capture.progress': 'Step {n} of {total}',
  'sv.capture.mandatory': 'Required',
  'sv.capture.optional': 'Optional — you can skip this',
  'sv.capture.clip': 'Short clip',
  'sv.capture.still': 'Photo',
  'sv.capture.orientation.LANDSCAPE': 'Hold your phone sideways (landscape)',
  'sv.capture.orientation.PORTRAIT': 'Hold your phone upright (portrait)',
  'sv.capture.orientation.ANY': 'Any orientation',
  'sv.capture.orientation.rotate': 'Rotate your phone to match',
  'sv.capture.duration': 'Record between {min} and {max} seconds',
  'sv.capture.durationMin': 'At least {min} seconds',
  'sv.capture.elapsed': '{s} s',
  'sv.capture.recording': 'Recording',
  'sv.capture.challenge.SHOW_NONCE': 'Show the code in frame',
  'sv.capture.challenge.SPEAK_NONCE': 'Say the code out loud',
  'sv.capture.challenge.ACTION': 'Do this while recording',
  'sv.capture.challenge.why': 'This shows the clip was recorded now, for this request.',
  'sv.capture.visitCode': 'Visit code {code}',
  'sv.capture.micOff':
    'Your microphone is off, so the spoken code cannot be heard. Allow the microphone, or the reviewer may ask you to record this again.',
  'sv.capture.camera.badge.rear': 'Rear camera',
  'sv.capture.camera.badge.front': 'Front camera',
  'sv.capture.camera.badge.camera': 'Camera',
  'sv.capture.camera.badge.preview': 'Preview',
  'sv.capture.camera.primer':
    'The camera turns on when you tap the button. Nothing is recorded until you start.',
  'sv.capture.camera.starting': 'Starting your camera…',
  'sv.capture.camera.checking': 'Checking camera permission…',
  'sv.capture.camera.notready': 'The camera is still starting — wait a moment and try again.',
  'sv.capture.motion.primer':
    'Your phone may ask to share motion data. It helps show the clip was recorded by hand, here.',
  'sv.capture.hint.lineup': 'Line up the shot and hold steady.',
  // ── capture chrome (SPEC_CAPTURE_UI_20260925): the bar, the grid + level, the location card ──
  'sv.action.flipShort': 'Switch',
  'sv.action.skipShort': 'Skip',
  'sv.capture.grid.label': 'Grid',
  'sv.capture.grid.hide': 'Hide the grid',
  'sv.capture.grid.show': 'Show the grid',
  'sv.capture.level.level': 'Level',
  'sv.capture.level.left': '{deg}° left',
  'sv.capture.level.right': '{deg}° right',
  'sv.capture.location.aria': 'Location that this shot will record',
  'sv.capture.location.waiting': 'Waiting for a location fix…',
  'sv.capture.location.denied': 'Location access is off — the shot is recorded without it',
  'sv.capture.location.unsupported': 'Location is not available on this device — the shot is recorded without it',
  'sv.capture.location.held': 'Location fix held for this shot',
  'sv.capture.location.bound': 'Bound to this shot',
  'sv.capture.location.accuracy': '± {m} m device-reported',
  'sv.capture.location.distance': 'Distance to the registered address: measured by the server after capture',
  'sv.capture.location.justNow': 'just now',
  'sv.capture.location.agoS': '{s} s ago',
  'sv.capture.location.agoMin': '{m} min ago',
  'sv.capture.location.footer': 'What this shot will record',
  'sv.action.details': 'Details',
  'sv.action.detailsHide': 'Hide details',
  // ── the stamp preview lines (SPEC_STAMP_ROUND §C.3, M3) — the band words are the be's own (SvStampComposer.bandWordsShort) ──
  'sv.capture.stamp.plusCode': 'Plus code {code}',
  'sv.capture.stamp.distance.about': 'about {d} from the registered address',
  'sv.capture.stamp.band.B50': 'within 50 m',
  'sv.capture.stamp.band.B200': 'within 200 m',
  'sv.capture.stamp.band.B1000': 'within 1 km',
  'sv.capture.stamp.band.OVER': 'more than 1 km',
  'sv.capture.stamp.distance.centroid': 'Distance to the registered address: not established (pincode centroid only)',
  'sv.capture.stamp.copyNote': 'Coordinates not printed on shared copies',
  'sv.capture.stamp.address': 'Registered address (as held by {tenantName})',
  // the live refresh (§C.4): why the card is not updating — the fix shown is the last one, with its real age
  'sv.capture.stamp.watch.denied': 'Location access is off — showing the last fix this phone gave',
  'sv.capture.stamp.watch.unavailable': 'Location updates unavailable — showing the last fix',
  'sv.capture.stamp.watch.timeout': 'No new fix yet — showing the last fix',
  'sv.capture.stamp.watch.unsupported': 'Location updates are not available on this device — showing the last fix',
  // the map thumbnail (the be's own tile): only the failure is said; OFF / HIDDEN says nothing
  'sv.capture.stamp.map.unavailable': 'Map not available right now',
  // the expanded map sheet (3×3 mosaic of the be's own tiles; no pan or zoom in this cut)
  'sv.capture.stamp.map.title': 'Map',
  'sv.capture.stamp.map.open': 'Open the map',
  'sv.capture.stamp.map.close': 'Close the map',
  'sv.capture.stamp.map.here': 'You are here',
  'sv.capture.stamp.map.fixed': 'Fixed view around your position — no pan or zoom in this version',
  'sv.capture.stamp.map.anchorOff': 'The registered address is off this map, {side}',
  'sv.capture.stamp.map.side.N': 'to the north',
  'sv.capture.stamp.map.side.NE': 'to the north-east',
  'sv.capture.stamp.map.side.E': 'to the east',
  'sv.capture.stamp.map.side.SE': 'to the south-east',
  'sv.capture.stamp.map.side.S': 'to the south',
  'sv.capture.stamp.map.side.SW': 'to the south-west',
  'sv.capture.stamp.map.side.W': 'to the west',
  'sv.capture.stamp.map.side.NW': 'to the north-west',
  'sv.capture.hint.selfie': 'Keep your whole face inside the oval, in even light.',
  'sv.capture.hint.doc': 'Keep the whole document inside the box, flat, with no glare.',
  'sv.capture.doc.guide.title': 'Before you photograph the document',
  'sv.capture.doc.guide.frame':
    'Lay the card flat and keep the whole card inside the box, with all four corners showing.',
  'sv.capture.doc.guide.glare':
    'Avoid glare and shadows: turn away from windows and lamps, and keep the flash off.',
  'sv.capture.doc.guide.lens':
    'Use the rear camera, hold the phone steady, and let it focus before you take the photo.',
  'sv.capture.doc.stored':
    'Your document photo is stored as evidence of this visit, under the same protections as every shot you take here, and only the people reviewing your case see it. Wherever the number is written out, only its last 4 characters are shown.',
  'sv.capture.doc.storedMasked':
    'Before this photo is stored, the first 8 digits of an Aadhaar number, any VID and any QR code on it are blacked out. Only the people reviewing your case see it, and the number is only ever written out as its last 4 characters.',
  // F-S7: an ID step the be does not mask (masking: NOT_APPLICABLE_POLICY — a Gulf / tenant-pack case)
  'sv.capture.doc.storedPolicy':
    'Your ID photo is stored as evidence of this visit, under the same protections as every shot you take here, and only the people reviewing your case see it.',
  'sv.capture.hint.dark': 'It looks too dark — find more light.',
  'sv.capture.hint.bright': 'It looks too bright — move out of direct light.',
  'sv.capture.hint.blurry': 'It looks blurry — hold steady and let the camera focus.',
  'sv.capture.assessing': 'Checking quality on your device…',
  'sv.capture.preview.ok': 'Looks usable — use it, or take it again.',
  'sv.capture.preview.notEvaluated':
    'Quality could not be checked on this device. It will be checked by the reviewer.',
  'sv.capture.face.none': "We couldn't see a face. Take the photo again with your face in the oval.",
  'sv.capture.face.notEvaluated': 'The face check will run when your photo is reviewed.',
  'sv.capture.queued': 'Saved on this phone — uploading in the background.',
  'sv.capture.uploading': '{n} pieces still uploading',
  'sv.capture.refused.title': 'This shot could not be accepted as sent',
  'sv.capture.refused.body':
    'The server could not confirm this recording matches what your phone sent, so it was not accepted. Please record it again.',
  'sv.capture.refused.code': 'Reason code {code}',
  'sv.capture.refused.doc.title': "We couldn't use this photo of your document",
  'sv.capture.refused.doc.body':
    "The photo isn't clear enough, or the document isn't one we can accept. Take it again with the document flat and in good light. We accept Aadhaar, PAN, passport, driving licence or voter ID.",
  'sv.capture.refused.doc.bodyPolicy':
    "The photo isn't clear enough to use. Take it again with the whole document flat and in good light.",
  'sv.capture.refused.photo.title': "We couldn't use this photo",
  'sv.capture.refused.photo.body': 'This photo could not be opened. Please take a new one.',
  'sv.capture.sealed': 'Accepted',
  'sv.capture.footer':
    'Recorded on your device, quality-checked here, and sealed with a hash chain on upload.',
  'sv.capture.bystander.title': 'Before you record inside',
  'sv.capture.bystander.body':
    'Ask anyone who does not need to be in the recording to step aside. If people must appear, that is fine — their faces are blurred on shared copies.',
  'sv.capture.bystander.cta': 'Understood — continue',
  'sv.capture.recaptureOnly':
    'Only the shots below were requested again. Everything else you sent is kept.',
  'sv.capture.signature.primer':
    'Your phone may ask for your fingerprint or face to sign the recording. This signs it with a key on this device; it does not share your fingerprint.',
  'sv.capture.signature.unavailable':
    'This device cannot sign recordings. Your capture is still accepted; the reviewer sees that no device signature was possible.',

  // ── edge quality gates ─────────────────────────────────────────────────────
  'sv.gate.clip.short': 'That clip is too short. Record for at least the minimum shown.',
  'sv.gate.clip.empty': 'Nothing was recorded — please record again.',
  'sv.gate.clip.cameraStopped': 'The camera stopped — record this step again.',
  'sv.gate.clip.dark': 'Most of that clip was too dark. Find more light and record it again.',
  'sv.gate.clip.bright': 'Most of that clip was too bright. Move out of direct light and record it again.',
  'sv.gate.clip.blurry': 'That clip was out of focus. Hold steady, let the camera focus, and record it again.',
  'sv.gate.clip.notEvaluated': 'Quality could not be checked on this device.',
  'sv.gate.still.small': 'This photo is too small for a clear copy. Move closer or use the rear camera.',
  'sv.gate.still.small.doc':
    'This photo is too small for the document to be readable. Move closer or use the rear camera.',
  'sv.gate.still.dark': 'This photo is too dark. Find more light and take it again.',
  'sv.gate.still.dark.doc': 'The document is too dark to read. Find more light and take it again.',
  'sv.gate.still.bright': 'This photo is too bright. Move out of direct light and take it again.',
  'sv.gate.still.glare.doc':
    'There is glare on the document. Move away from direct light, keep the flash off, and take it again.',
  'sv.gate.still.blurry': 'This photo is blurry. Hold steady, let the camera focus, and take it again.',
  'sv.gate.still.blurry.doc':
    'The document is blurry. Hold the phone steady, let the camera focus, and take it again.',
  'sv.gate.still.unreadable': "We couldn't read that photo. Please take it again.",

  // ── camera blocked / permissions ───────────────────────────────────────────
  'sv.cam.denied.title': 'Camera access is blocked',
  'sv.cam.denied.body':
    'Your browser has camera access turned off for this page. Follow the steps below, then tap the button.',
  'sv.cam.osdenied.title': 'Your device is blocking the camera',
  'sv.cam.osdenied.body':
    "The permission prompt never appeared, which usually means a system setting is blocking the browser's camera.",
  'sv.cam.nodevice.title': 'No camera found',
  'sv.cam.nodevice.fallback': 'We could not find a usable camera on this device.',
  'sv.cam.nodevice.hookDetail': 'No camera was found on this device.',
  'sv.cam.stalled.title': 'Your camera isn\'t sending a picture — tap to restart it.',
  'sv.cam.stalled.restart': 'Restart camera',
  'sv.cam.webview.title': 'Open this link in your browser',
  'sv.cam.webview.body':
    "This app's built-in browser cannot use the camera. Copy the link and open it in Chrome or Safari.",
  'sv.cam.unsupported.title': 'This browser cannot record video',
  'sv.cam.unsupported.body': 'Please open the link in a recent version of Chrome or Safari.',
  'sv.perm.steps.aria': 'Steps to allow the camera',
  'sv.perm.webview.tip': 'Tip: tap the menu (⋮ or the share icon) and choose "Open in browser".',
  'sv.perm.rec.os.1': "Open your phone's Settings.",
  'sv.perm.rec.os.2': 'Find your browser (Chrome or Safari) and open its permissions.',
  'sv.perm.rec.os.3': 'Turn on Camera and Microphone, then come back here.',
  'sv.perm.rec.chrome.1': 'Tap the lock icon next to the address.',
  'sv.perm.rec.chrome.2': 'Tap Permissions, then Camera.',
  'sv.perm.rec.chrome.3': 'Choose Allow, then tap the button below.',
  'sv.perm.rec.firefox.1': 'Tap the lock icon next to the address.',
  'sv.perm.rec.firefox.2': 'Tap Site permissions.',
  'sv.perm.rec.firefox.3': 'Set Camera to Allow, then tap the button below.',
  'sv.perm.rec.safari.1': 'Tap the "AA" icon in the address bar.',
  'sv.perm.rec.safari.2': 'Tap Website Settings.',
  'sv.perm.rec.safari.3': 'Set Camera to Allow, then tap the button below.',
  'sv.perm.rec.generic.1': "Open your browser's site settings for this page.",
  'sv.perm.rec.generic.2': 'Find Camera in the permissions list.',
  'sv.perm.rec.generic.3': 'Set it to Allow, then tap the button below.',
  'sv.perm.osnotice.ios':
    'Your device is below the minimum we support ({min}). You can try, but if the camera fails, please update or use another phone.',
  'sv.perm.osnotice.android':
    'Your browser is below the minimum we support ({min}). You can try, but if the camera fails, please update or use another phone.',
  'sv.perm.osnotice.samsung':
    'Your browser is below the minimum we support ({min}). You can try, but if the camera fails, please update or use another phone.',

  // ── location ───────────────────────────────────────────────────────────────
  // US-03482 P1-3: the location gate — no shot starts until the phone has given a position.
  'sv.geo.gate.title': 'Turn on location to continue',
  'sv.geo.gate.why': 'Every photo and video records where it was taken, so {tenantName} can confirm the premises.',
  'sv.geo.gate.waiting': 'Getting your location…',
  'sv.geo.gate.off.lead': "We can't get your location. Your phone's Location may be switched off.",
  'sv.geo.gate.denied.lead': 'Location is blocked for this page. Allow it to continue.',
  'sv.geo.gate.off.android.1': 'Swipe down from the top of your screen to open quick settings.',
  'sv.geo.gate.off.android.2': 'Tap Location to turn it on.',
  'sv.geo.gate.off.android.3': 'Come back here. We will check again on our own, or tap Try again.',
  'sv.geo.gate.off.ios.1': "Open your phone's Settings.",
  'sv.geo.gate.off.ios.2': 'Go to Privacy & Security, then Location Services, and turn it on.',
  'sv.geo.gate.off.ios.3': 'Come back here. We will check again on our own, or tap Try again.',
  'sv.geo.gate.off.other.1': "Turn on location (GPS) in your device's settings.",
  'sv.geo.gate.off.other.2': 'Make sure this browser is allowed to use your location.',
  'sv.geo.gate.off.other.3': 'Come back here. We will check again on our own, or tap Try again.',
  'sv.geo.gate.off.android.app':
    'Location already on? Open Settings, then Apps, then your browser (for example Chrome), then Permissions, then Location, and choose Allow.',
  'sv.geo.gate.off.ios.app': 'In the same list, tap {app} and choose While Using the App.',
  'sv.geo.gate.retry': 'Try again',
  'sv.geo.gate.ios.lead': 'Location was not allowed for this page.',
  'sv.geo.gate.ios.1': 'Tap Reload below to reload this page.',
  'sv.geo.gate.ios.2': 'When your phone asks to use your location, tap Allow.',
  'sv.geo.gate.ios.3': 'Not asked? Open Settings, then Privacy & Security, then Location Services, and make sure it is on — also for {app}.',
  'sv.geo.gate.reload': 'Reload',
  'sv.geo.gate.refining': 'Getting a more precise location — about ±{meters} m so far…',
  'sv.geo.gate.checking': 'Checking again…',
  'sv.geo.gate.timeout.lead': 'We\'re still looking for your position. Move near a window or step outside — we keep trying.',
  'sv.geo.gate.dismissed.lead': 'Your browser asked to use your location and it wasn\'t allowed. Tap Try again, then tap Allow when your browser asks.',
  'sv.geo.gate.asking.lead': 'Your browser is asking to use your location. Tap Allow when it asks — or tap Try again to be asked again.',
  'sv.geo.gate.webview.lead':
    "This app's built-in browser can't share your location or use the camera. Copy the link and open it in Chrome or Safari.",

  // ── review & submit ────────────────────────────────────────────────────────
  'sv.review.title': 'Ready to submit',
  'sv.review.sub':
    'Everything below is saved on this phone. Uploads finish in the background before submission.',
  'sv.review.waiting': 'Waiting for uploads to finish…',
  'sv.review.missing.title': 'Some required shots are missing',
  'sv.review.missing.body': 'The server needs these before it can accept your submission:',
  'sv.review.goTo': 'Record it now',
  'sv.review.submitFail':
    "We couldn't submit right now. Your recordings are safe on this phone — try again.",
  // DEC-097 T5 — the badge on the pre-submit review row says what THIS PHONE did, never what
  // anyone decided: nothing has been reviewed at this point, by a person or an engine.
  'sv.review.item.done': 'Sent',
  'sv.review.item.pending': 'Sending',
  'sv.review.item.refused': 'Take again',
  'sv.review.item.skipped': 'Skipped',

  // ── receipt ────────────────────────────────────────────────────────────────
  'sv.done.title': 'Submitted',
  'sv.done.sub': 'Thank you. Your recordings have been received and will be reviewed by a person.',
  'sv.done.reference': 'Reference',
  'sv.done.receipt': 'Receipt',
  'sv.done.captured': 'The steps you completed',
  'sv.done.next': 'What happens next',
  'sv.done.next.body':
    'A reviewer at {tenantName} checks the recordings. They decide the outcome — no automated system does. If more is needed, you will get a new link.',
  'sv.done.concern.title': 'Raise a concern',
  'sv.done.concern.body':
    'If anything about this request worried you, contact {tenantName} through a channel you already trust and quote the reference above.',
  'sv.done.limits.title': 'What this record shows',
  'sv.done.limits.chain':
    'Each recording was sealed with a hash chain on your phone as it was sent, bound to the time, location and this request. That shows the server received exactly what this phone sent, in order.',
  'sv.done.limits.signed': 'The recordings were also signed by a key on this device.',
  'sv.done.limits.unsigned': 'This device could not sign the recordings; the reviewer sees that.',
  'sv.done.limits.geo.missing': 'No location was recorded on this phone; the reviewer sees that.',
  'sv.done.memory':
    'Uploads on this device do not survive closing the tab. Keep it open until every shot shows Sent.',
  'sv.done.close': 'You can close this page.',

  // ── live leg (SV-5-2): the officer-directed recorded call ──────────────────
  'sv.live.card.title': 'Join the live verification',
  'sv.live.card.body':
    'An officer has asked to see your premises on a recorded call. Join when you are at the premises and ready.',
  'sv.live.card.action': 'Join now',
  'sv.live.title': 'Join the live verification',
  'sv.live.sub': 'An officer from {tenantName} will see and speak with you on a recorded call.',
  'sv.live.pill.recording': 'Recording',
  'sv.live.waiting.officer': 'Waiting for an officer to join…',
  'sv.live.connecting': 'Connecting your camera and microphone…',
  'sv.live.waiting.body':
    'An officer has not taken your call yet. This page checks every few seconds and connects you the moment one does.',
  'sv.live.waiting.keepOpen': 'Keep this page open. Nothing is recorded until the officer joins.',
  'sv.live.expect.title': 'What to expect',
  'sv.live.expect.officer': 'The officer will see you and your premises through your camera.',
  'sv.live.expect.prompts': 'They may ask you to show parts of the premises. Their requests appear on this screen as text.',
  'sv.live.expect.recorded': 'The call is recorded and kept by {tenantName} with your earlier recordings.',
  'sv.live.action.leave': 'Leave for now',
  'sv.live.action.hangUp': 'Leave the call',
  'sv.live.action.rejoin': 'Rejoin the call',
  'sv.live.reconnecting': 'Reconnecting…',
  'sv.live.officerHere': 'The officer is on the call.',
  'sv.live.officerJoining': 'Connected. The officer is joining.',
  'sv.live.media.permission':
    "We can't reach your camera or microphone. Please allow access in your browser, then rejoin.",
  'sv.live.media.other': "We're having trouble connecting your camera and microphone. Please retry in a moment.",
  'sv.live.prompts.title': "The officer's requests",
  'sv.live.prompts.none': 'No requests yet. The officer will guide you.',
  // the instruction card (SV-5-2 second round): a heading by kind, the officer's text, a Done acknowledgement
  'sv.live.prompt.kind.PAN': 'Pan the camera slowly',
  'sv.live.prompt.kind.HOLD': 'Hold the camera steady',
  'sv.live.prompt.kind.WALK': 'Walk with the camera on',
  'sv.live.prompt.kind.READ': 'Read aloud',
  'sv.live.prompt.kind.OTHER': 'The officer asks',
  'sv.live.prompt.done': "Done — I've shown this",
  'sv.live.prompt.history': 'Earlier requests',
  'sv.live.prompt.state.COMPLETED': 'Done',
  'sv.live.prompt.state.EXPIRED': 'Time ran out',
  'sv.live.prompt.state.CANCELLED': 'Withdrawn by the officer',
  'sv.live.consent.lead': "Before you join, the officer's live call needs your consent to the items below.",
  'sv.live.ended.title': 'The officer ended the session',
  'sv.live.ended.body':
    'Thank you. The call has been recorded and added to your request with {tenantName}. A person will review it.',
  'sv.live.left.title': 'You left the call',
  'sv.live.left.body': 'The officer may still be waiting. You can rejoin while the session is open.',
  'sv.live.unavailable.title': 'There is no live call to join',
  'sv.live.unavailable.body':
    'This request is not waiting for a live call. If you were asked to join one, contact the organisation that sent the link.',
  'sv.live.superseded.title': 'This call was opened on another device',
  'sv.live.superseded.body': 'Continue there. This page has been disconnected.',
  'sv.live.unreachable.title': "We couldn't reach the call",
  'sv.live.unreachable.body': 'Check your connection and try again.',

  // ── ROUND 2 (SPEC_CAPTURE_UX_R2, phases 1 + 2) — controls are verb phrases ≤ 4 words; no internal vocabulary ──
  // help layer (§7)
  'sv.help.button': 'Help',
  'sv.help.title': 'Help',
  'sv.help.close': 'Close',
  'sv.help.why.title': 'Why this shot?',
  'sv.help.why.generic': 'This helps {tenantName} confirm the place is real and in use.',
  'sv.help.why.EXT': 'This helps {tenantName} match your shop name and board to their records.',
  'sv.help.why.ENTRANCE': 'This shows {tenantName} the way in — that the door is real and reachable.',
  'sv.help.why.INT': 'This shows {tenantName} the working area from the inside, once around.',
  'sv.help.why.DOOR_PLATE': 'This lets {tenantName} read the unit or door number they have on record.',
  'sv.help.why.CERT_WALL': 'This lets {tenantName} see the registrations you display.',
  'sv.help.why.SIGNBOARD': 'This lets {tenantName} read the business name and GST number clearly from a sharp photo.',
  'sv.help.why.ACTIVITY': 'This shows {tenantName} that work goes on here day to day.',
  'sv.help.why.INSTALL_POINT': 'This shows {tenantName} where the connection will go.',
  'sv.help.why.HOME': 'This shows {tenantName} the home address you gave, from the outside.',
  'sv.help.why.REP_SELFIE': 'This lets {tenantName} see who is doing the verification.',
  'sv.help.why.ID_DOCUMENT': 'This lets {tenantName} match your name to the document you hold.',
  'sv.help.why.CHALLENGE': 'This shows the recording was made now, for this request.',
  'sv.help.why.screen': 'This checks that the place {tenantName} has on record is real and in use.',
  'sv.help.good.title': 'What a good one looks like',
  'sv.help.showMe': 'Show me',
  'sv.help.cap.generic': 'Line up the shot',
  'sv.help.cant.title': "What to do if you can't",
  'sv.help.skip.title': 'Skip this shot',
  'sv.help.skip.why': 'Tell us why (optional)',
  'sv.help.skip.reason.reach': "I can't reach this spot",
  'sv.help.skip.reason.none': "This doesn't exist here",
  'sv.help.skip.reason.unsafe': "It isn't safe right now",
  'sv.help.skip.reason.other': 'Something else',
  'sv.help.skip.confirm': 'Skip this',
  'sv.help.skip.note': 'Skipping an optional shot never blocks your request.',
  'sv.help.call.line': 'Need help? Ask us to call you.',
  'sv.help.call.button': 'Ask for a call',
  'sv.help.call.reason': 'What do you need help with? (optional)',
  'sv.help.call.sending': 'Sending…',
  'sv.help.call.failed': "We couldn't send that. Try again.",
  'sv.help.recovery.title': 'Camera or location blocked?',
  // recovery cards (§7.4)
  'sv.perm.platform.android': 'On Android (Chrome)',
  'sv.perm.platform.ios': 'On iPhone',
  'sv.perm.platform.other': 'In your browser',
  'sv.perm.ios.app.other': 'your browser app',
  'sv.perm.cam.title': 'Turn the camera on',
  'sv.perm.cam.ios.1': "Open your phone's Settings.",
  'sv.perm.cam.ios.2': 'Go to {app}, then Camera.',
  'sv.perm.cam.ios.3': "Choose 'Allow', then come back here and tap the button below.",
  'sv.perm.loc.title': 'Turn location on',
  'sv.perm.loc.android.1': 'Tap the lock icon next to the address.',
  'sv.perm.loc.android.2': 'Tap Permissions, then Location.',
  'sv.perm.loc.android.3': 'Choose Allow, then come back here.',
  'sv.perm.loc.ios.1': "Open your phone's Settings.",
  'sv.perm.loc.ios.2': 'Go to Privacy & Security, then Location Services, then {app}.',
  'sv.perm.loc.ios.3': "Choose 'While Using the App', then come back here.",
  'sv.perm.loc.other.1': "Open your browser's site settings for this page.",
  'sv.perm.loc.other.2': 'Find Location in the permissions list.',
  'sv.perm.loc.other.3': 'Set it to Allow, then come back here.',
  'sv.perm.rec.then': 'Nothing is lost while you do this.',
  'sv.capture.location.howTo': 'How to allow',
  // "saved, will send" (§8 #1)
  'sv.queue.saved': 'Saved on this phone · will send',
  'sv.queue.savedOffline': "Saved on this phone · will send when you're back online",
  'sv.queue.keepOpen': "Keep this page open until it's sent",
  'sv.queue.onePiece': '1 piece still uploading',
  // companion mode (§8 #2)
  'sv.consent.companion': "You can do this with a family member's help — just don't include them in the shots.",
  // language chooser (§2)
  'sv.lang.title': 'Choose your language',
  'sv.lang.sub': 'You can change it any time.',
  'sv.lang.suggested': 'Suggested',
  'sv.lang.lowData.title': 'Use less data',
  'sv.lang.lowData.sub': 'Maps and demonstrations load only when you ask.',
  'sv.lang.lowData.auto': 'On for this connection',
  // framing hints (§5) and the affirmation
  'sv.hint.dark': "It's a bit dark",
  'sv.hint.moving': 'Hold still',
  'sv.hint.closer': 'Move closer',
  'sv.hint.back': 'Move back',
  'sv.affirm.good': 'Good shot',
  // low-data map (§7.4)
  'sv.capture.stamp.map.show': 'Show map',
  // review: a skipped shot's reason
  'sv.review.skipReason': 'Skipped — {reason}',

  // ── ROUND 2, PHASE 3 — the demonstrations (§4) and the spoken guidance (§3) ───────────────────────
  // The eight captions are the §4 table, verbatim: one caption per demonstration, shown under the art (never
  // baked into it), and the only caption source there is.
  'sv.showme.STREET.cap': 'Stand across the street',
  'sv.showme.SIDEWAYS.cap': 'Turn your phone sideways',
  'sv.showme.DOOR.cap': 'Walk to the door',
  'sv.showme.TURN.cap': 'Turn around slowly, once',
  'sv.showme.PLATE.cap': 'Get close to the name plate',
  'sv.showme.CERTIFICATES.cap': 'Show the certificates on the wall',
  'sv.showme.FACE.cap': 'Look straight at the camera',
  'sv.showme.ID_FLAT.cap': 'Hold your ID flat and well-lit',
  'sv.showme.title': 'How to take this one',
  'sv.showme.gotIt': 'Got it',
  'sv.showme.close': 'Close',
  'sv.showme.next': 'Next',
  'sv.showme.again': 'Show again',
  // spoken guidance (§3.1 aria-labels are the spec's own words; §3.4 rung 3 is the honest note)
  'sv.audio.replay': 'Play the instructions again.',
  'sv.audio.mute': 'Turn guidance sound off',
  'sv.audio.unmute': 'Turn guidance sound on',
  'sv.audio.unavailable': 'Audio guidance is not available in {language} yet — read the steps below.',
  'sv.audio.listenNotice': 'Listen to this notice',
  // §3.2's line, with ONE deviation stated plainly: the spec quotes "I agree and start" (FSD §2.6), while this
  // app's consent control reads `sv.consent.agree`. The label is passed in as {action} so the sentence quotes the
  // button the customer can actually see, in whatever language they chose.
  'sv.audio.listenNotice.aid':
    "You can listen to this notice or read it. Either way, you'll still need to tap '{action}' when you're ready.",
  'sv.audio.stop': 'Stop',
} as const;

export type SvCopyKey = keyof typeof SV_COPY;
